// Cookie consent popup GDPR-compliant
import { useState } from 'react';
import { useCookieConsentStore } from '@/store/cookieConsentStore';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { cn } from '@/utils';
import { Cookie, ChevronDown, ChevronUp, Shield, BarChart3, Megaphone } from 'lucide-react';

export function CookieConsentBanner() {
  const { hasDecided, acceptAll, rejectAll, setPreferences } = useCookieConsentStore();
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  if (hasDecided) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-fade-in p-3">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto animate-slide-up shadow-2xl">
        <CardBody className="space-y-4">
          {/* Header */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center shrink-0">
              <Cookie size={20} className="text-brand-600 dark:text-brand-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-bold text-slate-900 dark:text-white">{t('cookie.title')}</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t('cookie.your_privacy')}</p>
            </div>
          </div>

          {/* Messaggio */}
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{t('cookie.message')}</p>

          {/* Privacy text */}
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3 text-xs text-slate-500 dark:text-slate-400">
            {t('cookie.privacy_text')}
          </div>

          {/* Espandi personalizzazione */}
          {expanded && (
            <div className="space-y-2 animate-slide-down">
              {/* Necessary (sempre attivo) */}
              <ConsentRow
                icon={<Shield size={14} />}
                title={t('cookie.necessary')}
                desc={t('cookie.necessary_desc')}
                checked
                disabled
              />
              <ConsentRow
                icon={<BarChart3 size={14} />}
                title={t('cookie.analytics')}
                desc={t('cookie.analytics_desc')}
                checked={analytics}
                onChange={() => setAnalytics((v) => !v)}
              />
              <ConsentRow
                icon={<Megaphone size={14} />}
                title={t('cookie.marketing')}
                desc={t('cookie.marketing_desc')}
                checked={marketing}
                onChange={() => setMarketing((v) => !v)}
              />
            </div>
          )}

          {/* Azioni */}
          <div className="space-y-2 pt-2">
            {!expanded ? (
              <>
                <Button className="w-full h-11" onClick={acceptAll}>{t('cookie.accept_all')}</Button>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 h-10" onClick={rejectAll}>{t('cookie.reject')}</Button>
                  <Button variant="ghost" className="flex-1 h-10" onClick={() => setExpanded(true)}>{t('cookie.customize')}</Button>
                </div>
              </>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 h-10" onClick={() => setExpanded(false)}>
                  {t('cookie.accept_all')}
                </Button>
                <Button className="flex-1 h-10" onClick={() => setPreferences({ analytics, marketing })}>
                  {t('cookie.save_preferences')}
                </Button>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function ConsentRow({
  icon, title, desc, checked, disabled, onChange,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: () => void;
}) {
  return (
    <div className={cn('flex items-start gap-3 p-2.5 rounded-lg', disabled ? 'bg-slate-100 dark:bg-slate-800/40' : 'bg-slate-50 dark:bg-slate-800/60')}>
      <div className="text-slate-500 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{title}</p>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{desc}</p>
      </div>
      <label className={cn('inline-flex items-center cursor-pointer', disabled && 'cursor-not-allowed opacity-70')}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          className="sr-only"
        />
        <div className={cn('w-9 h-5 rounded-full relative transition-colors', checked ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-600')}>
          <div className={cn('absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform', checked && 'translate-x-4')} />
        </div>
      </label>
    </div>
  );
}
